import React, { createContext, useContext, ReactNode } from "react";
import { useAuthQuery } from "../hooks/useTSAuth";
import { AuthUser } from "../services/svc_auth";

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  // Additional states from TanStack Query
  error: any;
  isLoginPending: boolean;
  isLogoutPending: boolean;
  loginError: any;
  logoutError: any;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Use TanStack Query for all auth state management
  const authQuery = useAuthQuery();

  const value: AuthContextType = {
    user: authQuery.user,
    loading: authQuery.loading,
    isAuthenticated: authQuery.isAuthenticated,
    login: authQuery.login,
    logout: authQuery.logout,
    checkAuth: authQuery.checkAuth,
    error: authQuery.error,
    isLoginPending: authQuery.isLoginPending,
    isLogoutPending: authQuery.isLogoutPending,
    loginError: authQuery.loginError,
    logoutError: authQuery.logoutError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
